import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import { useMediaQuery } from "@mui/material";

import axios from "axios";
import BoutonAddPeriod from './BoutonAddPeriod.js';
import BoutonSavePeriod from './BoutonSavePeriod.js';

import { normalizePeriodes } from "../utils/normalizePeriodes.js";
import { logDev } from "../utils/logger.js";

import { useLanguage } from '../context/LanguageContext.js';

import SnackbarFeedback from "../pages/SnackbarFeedback.js";
import { useSnackbar } from "../hooks/useSnackbar.js";


// 🔹 Composant principal : FormulairePeriodes
// - Permet d’afficher, modifier et enregistrer des périodes de dates
// - Gère les droits selon le rôle (admin ou utilisateur)
// - Synchronise avec le backend via axios

const FormulairePeriodes = ({ groupeId, initialPeriodes = [], onPériodesValidées, role = "utilisateur" }) => {
  const { snackbar, setSnackbar, bottomNavHeight, showSnackbar } = useSnackbar();

  const handleClick = () => {
    showSnackbar({ message: "Action réussie", severity: "success" });
  };

  const isMobile = useMediaQuery("(max-width:600px)");
  const { t } = useLanguage();

  // ✅ État local des périodes
  // Si des périodes initiales existent → on les charge
  // Sinon → on initialise avec une période vide
  const [periodes, setPeriodes] = useState(
    initialPeriodes.length > 0 ? initialPeriodes : [{ start: "", end: "" }] 
  );
  const MAX_PERIODES = 5;

  // 🔄 Synchroniser les périodes si la prop `initialPeriodes` change
  useEffect(() => {
  if (initialPeriodes && initialPeriodes.length > 0) {
    logDev("[FormulairePeriodes] sync initialPeriodes", initialPeriodes);
    setPeriodes(normalizePeriodes(initialPeriodes));
  }
  }, [initialPeriodes]);

  // ✅ État : édition verrouillée ou non
  const [editionVerrouillée, setEditionVerrouillée] = useState(true);

  // 🔄 Déverrouiller uniquement si le rôle est "admin"
  useEffect(() => {
    setEditionVerrouillée(role !== "admin");
  }, [role]);

  logDev("[FormulairePeriodes] rendu", { groupeId, role, nbInitial: initialPeriodes.length });

  const formatDateInput = (isoDate) =>
  typeof isoDate === "string" ? isoDate.slice(0, 10) : "";

  // ✅ Formater une date ISO pour l’input type="date"
  const enregistrerSondage = async () => {
    if (role !== "admin") {
      showSnackbar({ message: t("AdminOnlyAction"), severity: "warning" });
    return;
    }

     logDev("[FormulairePeriodes] tentative enregistrement", periodes);
   
  // 🔍 Vérification de la validité des dates
  for (let i = 0; i < periodes.length; i++) {
  const { start, end } = periodes[i];

  if (!start || !end) {
    logDev("[FormulairePeriodes] période invalide", { index: i, start, end });
    showSnackbar({ message: `⛔ ${t("Period")} ${i + 1} : ${t("BothDatesRequired")}`, severity: "warning" });
    return;
  }

  if (new Date(end) <= new Date(start)) {
    logDev("[FormulairePeriodes] période inversée", { index: i, start, end });
    showSnackbar({ message: `⛔ ${t("Period")} ${i + 1} : ${t("EndDateAfterStart")}`, severity: "warning" });
    return;
  }

  const diffMs = new Date(end) - new Date(start);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays > 7) {
    logDev("[FormulairePeriodes] période trop longue", { index: i, start, end, diffDays });
    showSnackbar({
      message: `⛔ ${t("Period")} ${i + 1} : ${t("MaxPeriodExceeded")}`,
      severity: "warning"
    });
    return;
  }
  }

  // ✅ Si toutes les périodes sont valides → envoi au backend
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/titresperiodes/${groupeId}`, {
      periodes
    });
    logDev("[FormulairePeriodes] périodes enregistrées", res.data);

    // On verrouille l’édition après sauvegarde
    setEditionVerrouillée(true);

    // Callback vers le parent pour signaler la validation
    onPériodesValidées();
  } catch (err) {
    logDev("❌ Erreur enregistrement périodes", { message: err.message, stack: err.stack });
    showSnackbar({ message: t("SavePeriodFailed"), severity: "error" });
  }
};

// ✅ Rendu JSX
return (
  <Box className="periode-editor" sx={{ mt: 4 }}>
    {periodes.map((periode, i) => (
      <Box 
        key={i} 
        className="periode-set" 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 1, mb: 2,
          width: "100%", // pour que marginLeft:auto fonctionne bien
         }}
      >
        {/* Label période */}
        <span style={{ width: 90 }}>{t('Period')} {i + 1}</span>

        {/* Input date début */}
        <input
          type="date"
          value={formatDateInput(periode.start)}
          disabled={editionVerrouillée}
          onChange={(e) => {
            const updated = [...periodes];
            updated[i].start = e.target.value;
            setPeriodes(updated);
          }}
        />
        <span>au</span>

        {/* Input date fin */}
        <input
          type="date"
          value={formatDateInput(periode.end)}
          disabled={editionVerrouillée}
          min={periode.start}   // empêche de choisir une date avant le début
          onChange={(e) => {
            const updated = [...periodes];
            updated[i].end = e.target.value;
            setPeriodes(updated);
          }}
        />

        {/* ❌ Bouton de suppression individuel (uniquement si édition déverrouillée) */}
        {!editionVerrouillée && (
          <IconButton
            size="small"
            onClick={async () => {
              const periodeToDelete = periodes[i];
              const updated = periodes.filter((_, index) => index !== i);
                setPeriodes(updated);

            try {
            // Appel backend pour supprimer le menu lié à cette période
              await axios.delete(
                `${process.env.REACT_APP_API}/menusjournalier/${groupeId}`,
                { data: { start: periodeToDelete.start, end: periodeToDelete.end } }
              );
              logDev("[FormulairePeriodes] menu associé supprimé", periodeToDelete);
            } catch (err) {
          logDev("❌ Erreur suppression menu associé", { message: err.message, stack: err.stack });
          showSnackbar({ message: t("DeleteMenuFailed"), severity: "error" });
        }
      }}
        sx={{
          marginLeft: "auto", // pousse le bouton à droite
          color: "#fff  !important",    
          backgroundColor: "#3399FF !important",          // texte
          borderColor: "#3399FF",        // bordure
            '&:hover': {
            backgroundColor: "#027bb5",  // fond au survol
            color: "#fff",               // texte au survol
            }, 
          width: isMobile ? 20 : 28,   // plus petit sur mobile
          height: isMobile ? 20 : 28,
          padding: 0,                  // pas de padding interne
          borderRadius: "4px",
          }}
          >
          <RemoveIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
    ))}

    {/* ➕ Ajouter une période + 💾 Enregistrer (uniquement si édition déverrouillée) */}
    {!editionVerrouillée && (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        gap: 3, 
        mt: 3, 
        alignItems: "center"
        }}>
        <BoutonAddPeriod
          label={t('AddPeriod')}
          /*onClick={() => setPeriodes([...periodes, { start: "", end: "" }])}*/
          onClick={() => {
            if (periodes.length >= MAX_PERIODES) {
              showSnackbar({
                message: `⛔ Maximum ${MAX_PERIODES} ${t("AuthorizedPeriods")} `,
                severity: "warning"
              });
              return;
            }
          setPeriodes([...periodes, { start: "", end: "" }]);
        }}
        />
        <BoutonSavePeriod
          label={t('SavePeriod')}
          onClick={enregistrerSondage}
        />
      </Box>
    )}

    <SnackbarFeedback
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        bottomNavHeight={bottomNavHeight}
      />
  </Box>
);
}

export default FormulairePeriodes;
